
import { useState } from 'react';
import { toast } from 'sonner';
import { metadataService } from '@/services/ai/metadataService';

export const useTagGenerator = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeFeature, setActiveFeature] = useState<string | null>(null);

  const generateTags = async (content: string, title: string) => {
    try {
      if (!content || content.trim().length === 0) {
        toast.error("Cannot generate tags for empty content. Please add some text to your article first.");
        return { 
          success: false, 
          error: "Cannot generate tags for empty content" 
        };
      }

      setIsProcessing(true);
      setActiveFeature('generateTags');
      
      console.log("Generating tags for content length:", content.length);
      console.log("Article title:", title);

      const response = await metadataService.generateTags(content, title);

      setIsProcessing(false);
      setActiveFeature(null);

      if (!response.success) {
        console.error("Error generating tags:", response.error);
        toast.error(`Failed to generate tags: ${response.error || "Unknown error"}`);
        return response;
      }

      if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
        console.error("Empty response from AI service");
        toast.error("Received empty response from AI service. Please try again.");
        return { 
          success: false, 
          error: "Empty response from AI service" 
        };
      }

      // Log the hierarchical tag structure for debugging
      console.log("Tag generation successful, response:", response.data);
      
      // Process the tags
      let tags = Array.isArray(response.data) ? response.data : 
                (response.data.tags && Array.isArray(response.data.tags)) ? response.data.tags : [];
                 
      // Quality control - ensure we have the most important tags
      const validatedTags = validateAndEnhanceTags(tags, title, content);
      
      return {
        success: true,
        data: validatedTags
      };
    } catch (error) {
      setIsProcessing(false);
      setActiveFeature(null);
      console.error("Error generating tags:", error);
      toast.error("Failed to generate tags. Please try again.");
      return { 
        success: false, 
        error: error instanceof Error ? error.message : "Unknown error" 
      };
    }
  };

  // New function to validate and enhance generated tags
  const validateAndEnhanceTags = (tags: any[], title: string, content: string) => {
    if (!tags || tags.length === 0) return [];
    
    // Make sure each tag has the required properties
    const processedTags = tags.map(tag => ({
      ...tag,
      relevance: tag.relevance || 5,
      level: tag.level || 'specific'
    }));
    
    // Verify we have at least one high-level tag
    const hasHighLevelTag = processedTags.some(tag => tag.level === 'high');
    
    if (!hasHighLevelTag) {
      console.log("No high-level tags found, identifying main subject from title");
      
      // Extract potential entities from title (naive approach - we'd use NER in a more sophisticated implementation)
      const words = title.split(/\s+/);
      const potentialEntities = [];
      
      for (let i = 0; i < words.length; i++) {
        // Check for capitalized words that might be entities
        if (words[i].length > 1 && words[i][0] === words[i][0].toUpperCase()) {
          // Try to capture multi-word entities (e.g., "Donald Trump")
          let entity = words[i];
          let j = i + 1;
          
          while (j < words.length && words[j].length > 1 && words[j][0] === words[j][0].toUpperCase()) {
            entity += " " + words[j];
            j++;
          }
          
          if (j > i + 1) {
            potentialEntities.push(entity);
            i = j - 1; // Skip the words we've already included
          } else {
            potentialEntities.push(words[i]);
          }
        }
      }
      
      // If we found potential entities, add the first one as a high-level tag
      if (potentialEntities.length > 0) {
        console.log("Adding main subject from title:", potentialEntities[0]);
        processedTags.unshift({
          tag: potentialEntities[0],
          relevance: 10,
          level: 'high'
        });
      } else {
        // If no entities found, upgrade the highest relevance tag to high-level
        const highestRelevance = Math.max(...processedTags.map(tag => tag.relevance || 0));
        
        processedTags.forEach(tag => {
          if ((tag.relevance || 0) === highestRelevance) {
            tag.level = 'high';
          }
        });
      }
    }
    
    // Sort tags by level (high first) and then by relevance
    return processedTags.sort((a, b) => {
      if (a.level === 'high' && b.level !== 'high') return -1;
      if (a.level !== 'high' && b.level === 'high') return 1;
      return (b.relevance || 0) - (a.relevance || 0);
    });
  };

  return {
    generateTags,
    isProcessing,
    activeFeature
  };
};
